import {
  executeValidationGrpc,
  getAvailableValidationsGrpc,
  getValidationSettingsGrpc,
} from '../../../shared/api/grpc/validationGrpcClient';
import { usePcfValidationStore } from '../../../shared/stores/usePcfValidationStore';
import { resolveActiveProjectKey } from '../../../shared/hooks/useActiveDocumentName';
import {
  applyValidationMetadata,
  buildValidationMappingOverridesFromStore,
  describeValidationContext,
  getSupersededRunReason,
  getValidationInvalidationEpoch,
  isSelectableValidationId,
  normalizeValidationRuntimeSettings,
  parseValidationReportJson,
  syncValidationMappingsToHost,
} from '../../pcf-validation';
import type { LiveValidationContext } from '../../pcf-validation';

export interface ExportValidationPreflightResult {
  success: boolean;
  exportReady: boolean;
  blockingIssueCount: number;
  warningIssueCount: number;
  totalIssueCount: number;
  error?: string;
}

const toIssueCount = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 0;
};

type HostCall = (method: string, params?: Record<string, unknown>) => Promise<unknown>;
export type ExportValidationContextSource = Omit<
  LiveValidationContext,
  'selectedValidationIds' | 'reconnectGapMarginMillimeters'
>;

const supersededResult = (reason: string): ExportValidationPreflightResult => ({
  success: false,
  exportReady: false,
  blockingIssueCount: 0,
  warningIssueCount: 0,
  totalIssueCount: 0,
  error: `Validation result discarded because ${reason}.`,
});

export async function runExportValidationPreflight(
  call: HostCall,
  readCurrentContext: () => ExportValidationContextSource
): Promise<ExportValidationPreflightResult> {
  usePcfValidationStore.getState().markReportStale();
  const invalidationEpochAtStart = getValidationInvalidationEpoch();
  const contextSourceAtStart = readCurrentContext();
  const validationsResult = await getAvailableValidationsGrpc();
  const settingsResult = await getValidationSettingsGrpc();
  if (!validationsResult.success || !settingsResult.success) {
    return supersededResult(
      validationsResult.error || settingsResult.error || 'the validation context could not be loaded'
    );
  }

  const selectedValidationIds = Array.from(new Set(
    (validationsResult.validations ?? [])
      .map((validation) => validation.id)
      .filter(isSelectableValidationId)
  ));
  const { reconnectGapMarginMillimeters } = normalizeValidationRuntimeSettings(settingsResult);
  const mappingOverrides = buildValidationMappingOverridesFromStore();
  const contextFingerprint = describeValidationContext({
    ...contextSourceAtStart,
    selectedValidationIds,
    reconnectGapMarginMillimeters,
    mappingOverrides,
  }).fingerprint;
  const getSupersededReason = (): string | null => getSupersededRunReason({
    invalidationEpochAtStart,
    runContextFingerprint: contextFingerprint,
    liveContextFingerprint: () => describeValidationContext({
      ...readCurrentContext(),
      selectedValidationIds,
      reconnectGapMarginMillimeters,
      mappingOverrides: buildValidationMappingOverridesFromStore(),
    }).fingerprint,
  });

  const contextLoadSupersededReason = getSupersededReason();
  if (contextLoadSupersededReason) {
    return supersededResult(contextLoadSupersededReason);
  }

  const mappingSyncResult = await syncValidationMappingsToHost(call, mappingOverrides);
  if (!mappingSyncResult.success) {
    return {
      success: false,
      exportReady: false,
      blockingIssueCount: 0,
      warningIssueCount: 0,
      totalIssueCount: 0,
      error: mappingSyncResult.error || 'The validation mappings could not be synchronized.',
    };
  }

  const mappingSyncSupersededReason = getSupersededReason();
  if (mappingSyncSupersededReason) {
    return supersededResult(mappingSyncSupersededReason);
  }

  const config = {
    enabledValidations: [],
    validateAllElements: true,
    stopOnFirstError: false,
    includeInfoMessages: true,
    includeWarnings: true,
    forceFresh: true,
    reconnectGapMarginMillimeters,
  };
  const result = await executeValidationGrpc(JSON.stringify(config));

  const validationSupersededReason = getSupersededReason();
  if (validationSupersededReason) {
    return supersededResult(validationSupersededReason);
  }

  if (!result.success || !result.reportJson) {
    return {
      success: false,
      exportReady: false,
      blockingIssueCount: 0,
      warningIssueCount: 0,
      totalIssueCount: 0,
      error: result.error || 'The validation runtime did not return a report.',
    };
  }

  try {
    const parsedReport = parseValidationReportJson(result.reportJson, 'export-preflight');
    const capturedDocumentKey = resolveActiveProjectKey({
      documentName: contextSourceAtStart.documentName,
      documentPath: contextSourceAtStart.documentPath,
    });
    const reportDocumentKey = resolveActiveProjectKey({
      documentName: parsedReport.DocumentName,
      documentPath: parsedReport.DocumentPath,
    });
    if (!capturedDocumentKey || reportDocumentKey !== capturedDocumentKey) {
      return supersededResult('the active document changed during the run');
    }
    const report = applyValidationMetadata(parsedReport, {
      lastValidationDate: parsedReport.ValidationDate,
    });
    const blockingIssueCount = toIssueCount(
      report.Dashboard?.BlockingIssueCount
        ?? (toIssueCount(report.CriticalIssues) + toIssueCount(report.ErrorIssues))
    );
    const warningIssueCount = toIssueCount(report.WarningIssues);
    const totalIssueCount = toIssueCount(report.TotalIssues);

    usePcfValidationStore.getState().setReport(report, contextFingerprint);

    return {
      success: true,
      exportReady: report.IsExportReady !== false && blockingIssueCount === 0,
      blockingIssueCount,
      warningIssueCount,
      totalIssueCount,
    };
  } catch (error) {
    return {
      success: false,
      exportReady: false,
      blockingIssueCount: 0,
      warningIssueCount: 0,
      totalIssueCount: 0,
      error: error instanceof Error ? error.message : 'The validation report could not be read.',
    };
  }
}
